import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui';
import {
  AutoAwesome as SparkleIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Lock as LockIcon,
} from '../ui/icons';
import { UseAssistantChat } from './useAssistantChat';

interface AssistantPanelProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  chat: UseAssistantChat;
}

const MAX_CHARS = 2000;
const EXAMPLE_KEYS = ['q1', 'q2', 'q3'] as const;

/**
 * Dockable AI assistant chat panel. Positioning mirrors NotificationPanel: it
 * anchors to the launcher button, opens to the side that has room, flips above
 * the anchor when it would overflow the bottom, and clamps on-screen — so it
 * never opens "dead" off-screen. Closes on click-outside and Escape.
 */
const AssistantPanel: React.FC<AssistantPanelProps> = ({ anchorEl, onClose, chat }) => {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState('');

  const { messages, usage, sending, error, quotaReached, send } = chat;

  // Anchored positioning + on-screen clamping (mirrors NotificationPanel). The
  // panel is wider/taller than the notification dropdown but uses the exact same
  // clamp logic so it stays fully visible on every viewport.
  const panelStyle = useMemo<React.CSSProperties>(() => {
    if (!anchorEl) return {};
    const rect = anchorEl.getBoundingClientRect();
    const M = 8; // viewport margin
    const GAP = 8;
    const W = Math.min(420, window.innerWidth - M * 2);
    const H = Math.min(620, window.innerHeight - M * 2);
    // Horizontal: a left-side anchor (desktop sidebar) opens to its right; a
    // right-side anchor (mobile top bar) right-aligns. Clamp on-screen.
    let left = rect.left < window.innerWidth / 2 ? rect.right + GAP : rect.right - W;
    left = Math.min(Math.max(M, left), window.innerWidth - W - M);
    // Vertical: open below if it fits, otherwise flip above the anchor.
    if (rect.bottom + GAP + H > window.innerHeight - M) {
      return { bottom: window.innerHeight - rect.top + GAP, left, width: W, maxHeight: H };
    }
    return { top: rect.bottom + GAP, left, width: W, maxHeight: H };
  }, [anchorEl]);

  // Close on click-outside.
  useEffect(() => {
    if (!anchorEl) return;
    const handler = (e: MouseEvent) => {
      if (
        !panelRef.current?.contains(e.target as Node) &&
        !anchorEl.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [anchorEl, onClose]);

  // Close on Escape.
  useEffect(() => {
    if (!anchorEl) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [anchorEl, onClose]);

  // Focus the input when the panel opens.
  useEffect(() => {
    if (anchorEl) inputRef.current?.focus();
  }, [anchorEl]);

  // Auto-scroll to the latest message (setting scrollTop is jsdom-safe).
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  if (!anchorEl) return null;

  const submit = (value: string) => {
    const text = value.trim();
    if (!text || sending || quotaReached) return;
    setDraft('');
    void send(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends; Shift+Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(draft);
    }
  };

  const isEmpty = messages.length === 0;
  // The paywall stub replaces the input once the monthly quota is reached.
  const showPaywall = quotaReached;

  // Portal to <body>: the sidebar/navbar ancestors use backdrop-filter, which
  // makes them the containing block for position:fixed descendants — the panel
  // would be positioned relative to (and clipped by) the sidebar instead of the
  // viewport (same fix as NotificationPanel).
  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label={t('assistant.title')}
      className="fixed z-[1400] flex flex-col rounded-2xl overflow-hidden border border-primary/20 shadow-pop bg-surface"
      style={panelStyle}
    >
      {/* Header */}
      <div className="px-4 py-3.5 flex justify-between items-center border-b border-line bg-primary/[0.03]">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary">
            <SparkleIcon size={18} />
          </div>
          <div className="leading-tight">
            <p className="text-sm font-black text-content">{t('assistant.title')}</p>
            <p className="text-[0.7rem] font-medium text-muted">{t('assistant.subtitle')}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label={t('common.close')}
          className="p-1.5 rounded-lg text-muted hover:text-primary hover:bg-primary/[0.08] transition-colors duration-150"
        >
          <CloseIcon size={18} />
        </button>
      </div>

      {/* Message list / empty state */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[160px]">
        {isEmpty ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted">{t('assistant.empty.intro')}</p>
            <div className="flex flex-col gap-2">
              {EXAMPLE_KEYS.map((key) => {
                const question = t(`assistant.empty.examples.${key}`);
                return (
                  <button
                    key={key}
                    onClick={() => submit(question)}
                    className="text-left text-sm rounded-xl border border-primary/20 bg-primary/[0.04] px-3 py-2 text-content hover:bg-primary/10 hover:border-primary/40 transition-colors duration-150"
                  >
                    {question}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
            >
              <div
                className={
                  m.role === 'user'
                    ? 'max-w-[85%] rounded-2xl rounded-br-md bg-primary text-white px-3.5 py-2 text-sm whitespace-pre-wrap break-words shadow-card'
                    : 'max-w-[85%] rounded-2xl rounded-bl-md bg-surface-2 border border-line text-content px-3.5 py-2 text-sm whitespace-pre-wrap break-words'
                }
              >
                {m.text}
              </div>
            </div>
          ))
        )}

        {/* Loading indicator while awaiting a reply */}
        {sending && (
          <div className="flex justify-start" aria-live="polite">
            <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-md bg-surface-2 border border-line px-3.5 py-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce [animation-delay:-0.3s]" />
              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce [animation-delay:-0.15s]" />
              <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" />
            </div>
          </div>
        )}

        {/* Non-quota error (quota is handled by the paywall in the footer) */}
        {error && error !== 'assistant.errors.quota_exceeded' && (
          <div className="rounded-xl border border-danger/30 bg-danger/[0.06] px-3.5 py-2.5 text-sm text-danger">
            {t(error)}
          </div>
        )}
      </div>

      {/* Footer: paywall stub OR input + quota + disclaimer */}
      <div className="border-t border-line bg-surface">
        {showPaywall ? (
          <div className="px-4 py-4">
            <div className="rounded-xl border border-primary/30 bg-primary/[0.06] px-3.5 py-3">
              <div className="flex items-center gap-2 mb-1">
                <LockIcon size={15} className="text-primary" />
                <span className="text-sm font-bold text-content">
                  {t('assistant.paywall.title')}
                </span>
                <span className="px-2 py-0.5 rounded-full text-[0.65rem] font-extrabold text-primary bg-primary/15">
                  {t('assistant.paywall.badge')}
                </span>
              </div>
              <p className="text-xs text-muted">{t('assistant.paywall.body')}</p>
            </div>
          </div>
        ) : (
          <div className="px-3 pt-3 pb-2">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value.slice(0, MAX_CHARS))}
                onKeyDown={handleKeyDown}
                rows={1}
                maxLength={MAX_CHARS}
                placeholder={t('assistant.placeholder')}
                aria-label={t('assistant.placeholder')}
                className="flex-1 resize-none max-h-28 rounded-xl border border-line bg-surface-2 px-3 py-2 text-sm text-content placeholder:text-muted/70 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all duration-200"
              />
              <Button
                onClick={() => submit(draft)}
                disabled={!draft.trim() || sending}
                loading={sending}
                size="sm"
                aria-label={t('assistant.send')}
                className="shrink-0 !px-3"
              >
                {!sending && <SendIcon size={16} />}
              </Button>
            </div>
            <div className="flex items-center justify-between gap-2 mt-2 px-1">
              <p className="text-[0.65rem] text-muted/80 leading-snug">
                {t('assistant.disclaimer')}
              </p>
              {usage && (
                <span className="shrink-0 text-[0.65rem] font-semibold text-muted whitespace-nowrap">
                  {t('assistant.quota_label', { used: usage.used, limit: usage.limit })}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default AssistantPanel;
